import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, LoaderCircle } from 'lucide-react';
import { ConfirmationModal } from '@/components/core/confirmation-modal/confirmation-modal';
import { Button } from '@/components/ui-kit/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { Textarea } from '@/components/ui-kit/textarea';
import { Label } from '@/components/ui-kit/label';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { useHasPermission } from '@/hooks/use-has-permission';
import { formatDateOnly, formatOrgDateTime } from '@/lib/utils/custom-date/custom-date';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/state/store/auth';
import {
  useArchiveLeaveRequest,
  useCancelLeaveRequest,
  useLeaveDocuments,
  useLeaveRequestBalance,
  useReviewLeaveRequest,
} from '../../hooks/use-leave-manager';
import { LeaveRequest } from '../../types/leave-manager.types';

const DetailItem = ({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: ReactNode;
}) => (
  <div className={cn('min-w-0 space-y-0.5', className)}>
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className="font-medium [overflow-wrap:anywhere]">{children}</p>
  </div>
);

export interface LeaveReviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  leaveRequest: LeaveRequest | null;
}

export const LeaveReviewDialog = ({ open, onOpenChange, leaveRequest }: LeaveReviewDialogProps) => {
  const { t } = useTranslation();
  const [note, setNote] = useState('');
  const [isRejecting, setIsRejecting] = useState(false);
  const {
    mutateAsync: reviewLeaveRequest,
    isPending: isReviewing,
    variables: reviewVariables,
  } = useReviewLeaveRequest();
  const { mutateAsync: archiveLeaveRequest, isPending: isArchiving } = useArchiveLeaveRequest();
  const { mutateAsync: cancelLeaveRequest, isPending: isCancelling } = useCancelLeaveRequest();
  const [isCancelConfirmOpen, setIsCancelConfirmOpen] = useState(false);
  const isPending = isReviewing || isArchiving || isCancelling;
  // Only the clicked button spins; the others are just disabled while it runs.
  const reviewingStatus = isReviewing ? reviewVariables?.status : undefined;
  const canReview = useHasPermission(HRM_PERMISSIONS.LEAVE.REVIEW);
  const canArchive = useHasPermission(HRM_PERMISSIONS.LEAVE.ARCHIVE);
  const canCancel = useHasPermission(HRM_PERMISSIONS.LEAVE.CANCEL);
  // The request's days counted now, and what was left of the leave type before it (the count
  // LEAVE_REQUEST_SUBMIT checks).
  const {
    data: requestBalance,
    isSuccess: isBalanceLoaded,
    isLoading: isBalanceLoading,
  } = useLeaveRequestBalance(open ? leaveRequest : null);
  const balance = requestBalance?.balance ?? null;
  const currentUserId = useAuthStore((state) => state.user?.itemId ?? '');
  // A sick leave's doctor's documents are medical data: only the employee and holders of the sick leave
  // permission (HR, as legacy showed them to HR admins only) see them.
  const canViewSickLeave = useHasPermission(HRM_PERMISSIONS.SICK_LEAVE.VIEW);
  const isOwn = Boolean(currentUserId) && leaveRequest?.UserId === currentUserId;
  const canSeeDocuments = open && (isOwn || canViewSickLeave);
  const { data: documents, isLoading: isDocumentsLoading } = useLeaveDocuments(
    canSeeDocuments && !leaveRequest?.IsDocumentSendByDr ? (leaveRequest?.FileIds ?? []) : []
  );

  if (!leaveRequest) return null;
  const isRequestPending = leaveRequest.Status === 'Pending';
  const duration = t(leaveRequest.LeaveType === 'HalfDay' ? 'HALF_DAY' : 'FULL_DAY');
  // LEAVE_REQUEST_REVIEW stores the reviewer's note; a rejection always has one (legacy showed it too).
  const rejectionNote =
    leaveRequest.Status === 'Rejected' && leaveRequest.Note?.trim() !== '-'
      ? leaveRequest.Note?.trim()
      : '';
  // Client-side mirror of the workflow's unconditional self-review block, so the button isn't even
  // offered; the workflow (LEAVE_REQUEST_REVIEW) enforces it regardless of this check. The request's
  // `UserId` is the employee (LEAVE_REQUEST_SUBMIT / SICK_LEAVE_REQUEST stamp it), so comparing the one
  // already fetched here is enough.
  const isOwnRequest = Boolean(currentUserId) && leaveRequest.UserId === currentUserId;
  const canShowReviewActions = canReview && !isOwnRequest;

  const close = () => {
    setIsRejecting(false);
    setNote('');
    onOpenChange(false);
  };

  // A refusal (self-review, the request was already decided, ...) comes back as `IsSuccessful: false`
  // with its own message, toasted by `useReviewLeaveRequest` / `useArchiveLeaveRequest`; the dialog
  // stays open then.
  const handleApprove = async () => {
    try {
      const result = await reviewLeaveRequest({ itemId: leaveRequest.ItemId, status: 'Approved' });
      if (result.IsSuccessful) close();
    } catch {
      // Error toast is already shown by the mutation's global error handler.
    }
  };

  const handleReject = async () => {
    if (!isRejecting) {
      setIsRejecting(true);
      return;
    }
    try {
      const result = await reviewLeaveRequest({
        itemId: leaveRequest.ItemId,
        status: 'Rejected',
        note,
      });
      if (result.IsSuccessful) close();
    } catch {
      // Error toast is already shown by the mutation's global error handler.
    }
  };

  // LEAVE_REQUEST_CANCEL refuses the caller's own request, another department's and one no longer Approved.
  const handleCancel = async () => {
    try {
      const result = await cancelLeaveRequest({ itemId: leaveRequest.ItemId });
      if (result.IsSuccessful) {
        setIsCancelConfirmOpen(false);
        close();
      }
    } catch {
      // Error toast is already shown by the mutation's global error handler.
    }
  };

  const handleArchive = async () => {
    try {
      const result = await archiveLeaveRequest(leaveRequest.ItemId);
      if (result.IsSuccessful) close();
    } catch {
      // Error toast is already shown by the mutation's global error handler.
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (!next ? close() : onOpenChange(next))}>
      <DialogContent className="sm:max-w-md" alignCloseWithTitle>
        <DialogHeader>
          <DialogTitle>{t('LEAVE_REQUEST_DETAILS')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          <div className="grid grid-cols-2 gap-x-4 gap-y-3 rounded-lg border p-3">
            <DetailItem label={t('LEAVE_TYPE')} className="col-span-2">
              {leaveRequest.Title}
            </DetailItem>
            <DetailItem label={t('START_DATE')}>
              {formatDateOnly(leaveRequest.StartDate)}
            </DetailItem>
            <DetailItem label={t('END_DATE')}>{formatDateOnly(leaveRequest.EndDate)}</DetailItem>
            <DetailItem label={t('DURATION')}>{duration}</DetailItem>
            {requestBalance && (
              <DetailItem label={t('TOTAL_DAYS')}>{requestBalance.days}</DetailItem>
            )}
            {isBalanceLoading && (
              <DetailItem label={t('LEAVE_BALANCE_BEFORE_REQUEST')}>
                <LoaderCircle
                  className="size-4 animate-spin text-muted-foreground"
                  aria-busy="true"
                />
              </DetailItem>
            )}
            {isBalanceLoaded && (
              <DetailItem
                label={t('LEAVE_BALANCE_BEFORE_REQUEST')}
                className={
                  balance && requestBalance.days > balance.remaining
                    ? 'text-destructive'
                    : undefined
                }
              >
                {balance
                  ? t('LEAVE_BALANCE_DAYS', { days: balance.remaining })
                  : t('LEAVE_BALANCE_UNLIMITED')}
              </DetailItem>
            )}
            {leaveRequest.CreatedDate && (
              <DetailItem label={t('REQUESTED_ON')}>
                {formatOrgDateTime(leaveRequest.CreatedDate, leaveRequest.Timezone || undefined)}
              </DetailItem>
            )}
          </div>

          {rejectionNote && (
            <div className="space-y-1.5">
              <p className="text-xs text-muted-foreground">{t('REJECTION_NOTE')}</p>
              <p className="whitespace-pre-wrap rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-destructive [overflow-wrap:anywhere]">
                {rejectionNote}
              </p>
            </div>
          )}

          {leaveRequest.Reason && (
            <div className="space-y-1.5">
              <p className="text-xs text-muted-foreground">{t('REASON')}</p>
              <p className="whitespace-pre-wrap rounded-lg bg-muted/50 p-3 [overflow-wrap:anywhere]">
                {leaveRequest.Reason}
              </p>
            </div>
          )}

          {leaveRequest.IsDocumentSendByDr && (
            <div className="space-y-1.5">
              <p className="text-xs text-muted-foreground">{t('UPLOADED_DOCUMENTS')}</p>
              <p className="font-medium">{t('FILE_SEND_BY_DOCTOR')}</p>
            </div>
          )}
          {isDocumentsLoading && (
            <div className="space-y-1.5">
              <p className="text-xs text-muted-foreground">{t('ATTACHED_FILES')}</p>
              <LoaderCircle
                className="size-4 animate-spin text-muted-foreground"
                aria-busy="true"
              />
            </div>
          )}
          {documents && documents.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-xs text-muted-foreground">{t('ATTACHED_FILES')}</p>
              <div className="space-y-1">
                {documents.map((document) => (
                  <a
                    key={document.id}
                    href={document.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 rounded-md border px-3 py-2 text-primary hover:bg-muted/50"
                  >
                    <FileText className="h-4 w-4 shrink-0" />
                    <span className="min-w-0 truncate">{document.name || t('DOWNLOAD_FILE')}</span>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>

        {isRequestPending && canShowReviewActions && isRejecting && (
          <div className="space-y-1.5">
            <Label htmlFor="leave-rejection-note">{t('REJECTION_NOTE')}</Label>
            <Textarea
              id="leave-rejection-note"
              rows={3}
              placeholder={t('REJECTION_NOTE')}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          {isRequestPending
            ? canShowReviewActions && (
                <>
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={handleReject}
                    loading={reviewingStatus === 'Rejected'}
                    disabled={isPending || (isRejecting && !note.trim())}
                  >
                    {t('REJECT')}
                  </Button>
                  {!isRejecting && (
                    <Button
                      type="button"
                      onClick={handleApprove}
                      loading={reviewingStatus === 'Approved'}
                      disabled={isPending}
                    >
                      {t('APPROVE')}
                    </Button>
                  )}
                </>
              )
            : !isOwnRequest && (
                <>
                  {canCancel && leaveRequest.Status === 'Approved' && (
                    <Button
                      type="button"
                      variant="destructive"
                      onClick={() => setIsCancelConfirmOpen(true)}
                      disabled={isPending}
                    >
                      {t('CANCEL_LEAVE')}
                    </Button>
                  )}
                  {canArchive && !leaveRequest.IsArchived && (
                    <Button
                      type="button"
                      onClick={handleArchive}
                      loading={isArchiving}
                      disabled={isPending}
                    >
                      {t('ARCHIVE')}
                    </Button>
                  )}
                </>
              )}
        </DialogFooter>
      </DialogContent>

      <ConfirmationModal
        open={isCancelConfirmOpen}
        onOpenChange={setIsCancelConfirmOpen}
        title={t('CANCEL_LEAVE_REQUEST')}
        description={t('ARE_YOU_SURE_WANT_TO_CANCEL_LEAVE')}
        confirmText={isCancelling ? t('CANCELLING') : t('CANCEL_LEAVE')}
        preventAutoClose={isCancelling}
        onConfirm={handleCancel}
      />
    </Dialog>
  );
};
